import {
  BULK_REQUEST_LIMIT,
  DEBOUNCE_TIME,
  DEFAULT_CATEGORY_FILTER,
  DEFAULT_REQUEST_LIMIT,
} from 'api/constants';
import { useGetBusinessPackages } from 'api/hooks/business';
import useGetSearchedUsers from 'api/hooks/user/useGetSearchedUsers';
import { Filters } from 'components/dumb/layouts/FiltersModal/FiltersForm';
import { AppMode, SearchFilters } from 'helpers/constants/enums';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import useDebounce from 'hooks/useDebounce';
import useExperiencesUrlParams from 'hooks/useExperiencesUrlParams';
import useInAppNavigate from 'hooks/useInAppNavigate';
import useOnEndReached from 'hooks/useOnEndReached';
import useRedirectToExperienceView from 'hooks/useRedirectToExperienceView';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { setAppMode } from 'redux/slices/appMode';
import {
  appViewRole,
  authUserOnboardingIsSkipedSelector,
  authUserSelector,
} from 'redux/slices/selectors';

import useGetCityByCountryId from 'api/hooks/country/useGetCityByCountryId';
import useGetCountries from 'api/hooks/country/useGetCountries';
import useGetLanguages from 'api/hooks/languages/useGetLanguages';
import { AppRole } from 'api/types/user';
import ExperienceFeedPageLayout from 'components/dumb/layouts/ExperienceFeedPageLayout';
import mapToOption from 'helpers/functions/mapToOption';
import PATH from 'routes/paths';
import {
  contentCreatorSlug,
  experienceSlug,
  userId,
} from '../../../../routes/paths/constants';
import { Option } from '../../../dumb/atomics/DropdownInput';

export default function BusinessDasboard() {
  const {
    urlParams,
    setUrlCategory,
    setFilterParamsToUrlFromObject,
    removeFilterParamsFromUrlFromObject,
    removeFilterParamsByKeysFromUrl,
  } = useExperiencesUrlParams();
  const dispatch = useAppDispatch();

  const [offset, setOffset] = useState(0);
  const limit = DEFAULT_REQUEST_LIMIT;
  const debouncedSearchTextParam = useDebounce(
    urlParams.searchText,
    DEBOUNCE_TIME
  );
  const [selectedCountry, setSelectedCountry] = useState<string>(
    urlParams.country
  );
  const [searchedCity, setSearchedCity] = useState('');
  const debouncedCity = useDebounce(searchedCity, DEBOUNCE_TIME);

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const [searchFilter, setSearchFilter] = useState<SearchFilters>(
    (urlParams.category as SearchFilters) || SearchFilters.EXPERIENCES
  );
  const [domainTabFilter, setDomainTabFilter] = useState(
    DEFAULT_CATEGORY_FILTER
  );
  const authUser = useAppSelector(authUserSelector);
  const { navigate } = useInAppNavigate();

  const handleSetSearchFilters = (filter: SearchFilters) => {
    setSearchFilter(filter);
    setUrlCategory(filter);
    setOffset(0);
  };

  const handleDomainFilter = (domain: string) => {
    setDomainTabFilter(domain);
    setOffset(0);
  };

  const handleApplyFilters = (filtersData: Filters) => {
    const hasNonDefaultFilters = Object.values(filtersData).some(
      (filter) =>
        filter &&
        filter !== DEFAULT_CATEGORY_FILTER &&
        filter.value !== DEFAULT_CATEGORY_FILTER
    );

    setFilterParamsToUrlFromObject(filtersData);

    if (hasNonDefaultFilters) {
      setDomainTabFilter(DEFAULT_CATEGORY_FILTER);
    }

    setOffset(0);
  };

  const handleClearFilters = (data: Filters) => {
    removeFilterParamsFromUrlFromObject(data);
  };

  const handleSelectedCountry = (country: Option) => {
    setSelectedCountry(country.value);
    removeFilterParamsByKeysFromUrl(['city']);
  };

  const handleSearchedCity = (cityInput: string) => {
    setSearchedCity(cityInput);
  };

  const {
    data: searchedUsers,
    fetchNextPage: fetchMoreUsers,
    isFetching: fetchingUsers,
    isFetchingNextPage: fetchingMoreUsers,
  } = useGetSearchedUsers(
    {
      limit,
      searchText: debouncedSearchTextParam as string,
    },
    Boolean(debouncedSearchTextParam)
  );

  const {
    data: pagedExperiences,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
  } = useGetBusinessPackages({
    limit,
    domain: urlParams.domain || domainTabFilter,
    searchText: debouncedSearchTextParam as string,
  });
  const { data: languageOptions, isLoading: loadingLanguages } =
    useGetLanguages(isFiltersOpen);
  const { data: countryOptions, isLoading: loadingCountries } =
    useGetCountries(isFiltersOpen);

  const { data: cityResponse } = useGetCityByCountryId(
    {
      id: selectedCountry ?? authUser?.city?.country.id ?? '',
      offset: 0,
      limit: BULK_REQUEST_LIMIT,
      searchText: debouncedCity as string,
    },
    !!selectedCountry
  );

  const handleNavigateProfile = (
    role: string,
    profileId: string,
    slug?: string
  ) => {
    switch (role) {
      case AppRole.ContentCreator:
        if (slug && slug.length > 0) {
          navigate(
            PATH.EXMINDER.VIEW_PROFILE_BY_SLUG.replace(contentCreatorSlug, slug)
          );
        } else {
          navigate(PATH.EXMINDER.VIEW_PROFILE.replace(userId, profileId));
        }
        break;
      case AppRole.Consumer:
        navigate(PATH.EXPLORER.VIEW_ONE.replace(userId, profileId));
        break;
      default:
        navigate(PATH.FAKE_INFLUENCER.VIEW_ONE.replace(userId, profileId));
        break;
    }
  };

  const isAtEnd = useOnEndReached();

  useEffect(() => {
    if (isAtEnd) {
      setOffset(offset + limit);
      fetchNextPage();
      if (searchFilter === SearchFilters.ACCOUNTS) {
        fetchMoreUsers();
      }
    }
  }, [searchFilter, isAtEnd]);

  useEffect(() => {
    if (!debouncedSearchTextParam) {
      setSearchFilter(SearchFilters.EXPERIENCES);
    }
  }, [debouncedSearchTextParam]);

  const appRole = useAppSelector(appViewRole);
  const isOnboardingSkipped = useAppSelector(
    authUserOnboardingIsSkipedSelector
  );

  useRedirectToExperienceView();

  const loading =
    isFetching ||
    isFetchingNextPage ||
    fetchingUsers ||
    fetchingMoreUsers ||
    loadingLanguages ||
    loadingCountries;

  useEffect(() => {
    dispatch(setAppMode(AppMode.BUSINESS));
  }, []);

  return (
    <>
      <ExperienceFeedPageLayout
        currentAppMode={AppMode.BUSINESS}
        handleAppModeToggleEnd={() => {
          navigate(PATH.INDIVIDUAL_INDEX);
        }}
        businessExperiences={pagedExperiences?.pages?.map((experience) => ({
          ...experience,
          startDate: new Date(),
          endDate: new Date(),
          duration: experience.duration,
          durationType: experience.durationType,
          location: experience.city,
          type: experience.department,
          company: experience.company,
          href: PATH.BUSINESS.EXPERIENCE_VIEW_ONE.replace(
            experienceSlug,
            experience.slug
          ),
        }))}
        filters={urlParams}
        activeDomainTab={domainTabFilter}
        searchFilter={searchFilter}
        searchText={debouncedSearchTextParam as string}
        users={[...(searchedUsers?.pages ?? [])]}
        countries={mapToOption(countryOptions)}
        cities={mapToOption(cityResponse)}
        languages={mapToOption(languageOptions)}
        isExminder={appRole === AppRole.ContentCreator}
        isLoading={loading}
        requestIsLoading={isFetching}
        isTextSearched={Boolean(debouncedSearchTextParam)}
        isOnboardingSkipped={isOnboardingSkipped}
        currentSearchFilter={searchFilter}
        onDomainFilter={handleDomainFilter}
        onSelectSearchFilter={handleSetSearchFilters}
        onCreateAuctionClick={() => navigate(PATH.EXMINDER.MY_EXPERIENCES)}
        onCreateExperienceClick={() => navigate(PATH.EXPERIENCE.CREATE)}
        onTurnOnNotifications={() => navigate(PATH.ACCOUNT_SETTINGS.INDEX)}
        onBuildProfile={() => navigate(PATH.ONBOARDING.CHAT)}
        onSelectedCountry={handleSelectedCountry}
        onSearchedCity={handleSearchedCity}
        onOpenFilters={setIsFiltersOpen}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        onShowExminderProfileClick={(creatorSlug: string) => {
          navigate(
            PATH.EXMINDER.VIEW_PROFILE_BY_SLUG.replace(
              contentCreatorSlug,
              creatorSlug
            )
          );
        }}
        onUserFilterResultClick={(
          role: string,
          profileId: string,
          slug?: string
        ) => {
          handleNavigateProfile(role, profileId, slug);
        }}
      />
      <Helmet>
        <meta name="theme-color" content="#ffffff" />
      </Helmet>
    </>
  );
}
